import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { tenantFetch } from "../lib/api-client";

interface Product {
  id: string;
  sku: string;
  title: string;
  brand: string;
  category: string;
  price: number;
  currency: string;
  image_url: string;
  status: string;
}

interface SponsoredCampaign {
  _key: string;
  brand_id: string;
  brand_name: string;
  name: string;
  status: "active" | "paused" | "deleted";
  created_at: string;
  budget: {
    daily_limit: number;
    monthly_limit: number;
    spent_today: number;
    spent_month: number;
    total_spent: number;
  };
  bidding: {
    model: "cpc" | "cpm";
    bid_amount: number;
    max_bid: number;
  };
  targeting: {
    categories: string[];
    keywords: string[];
    min_semantic_score: number;
    product_ids: string[];
    all_products: boolean;
  };
  boost_config: {
    max_position_boost: number;
    max_sponsored_slots: number;
  };
  performance: {
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number;
  };
}

// Use internal campaigns API
const CAMPAIGNS_API = "/api/v1/campaigns";

// Icons
const ArrowLeftIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      d="M19 12H5M12 19l-7-7 7-7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const XIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M5 5l10 10M15 5l-10 10" strokeLinecap="round" />
  </svg>
);

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

// Edit Modal Component
function EditCampaignModal({
  isOpen,
  onClose,
  campaign,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  campaign: SponsoredCampaign;
  onSave: (updates: Partial<SponsoredCampaign>) => Promise<void>;
}) {
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectAll, setSelectAll] = useState(
    campaign.targeting?.all_products ?? true,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: campaign.name,
    daily_budget: campaign.budget.daily_limit,
    monthly_budget: campaign.budget.monthly_limit,
    bid_amount: campaign.bidding.bid_amount,
    categories: campaign.targeting.categories?.join(", ") || "",
    keywords: campaign.targeting.keywords?.join(", ") || "",
  });

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectAll(campaign.targeting?.all_products ?? true);
      setSelectedProductIds(new Set(campaign.targeting?.product_ids || []));
      setFormData({
        name: campaign.name,
        daily_budget: campaign.budget.daily_limit,
        monthly_budget: campaign.budget.monthly_limit,
        bid_amount: campaign.bidding.bid_amount,
        categories: campaign.targeting.categories?.join(", ") || "",
        keywords: campaign.targeting.keywords?.join(", ") || "",
      });
      fetchProducts();
    }
  }, [isOpen, campaign]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await tenantFetch(
        "/api/products?status=active&limit=1000",
      );
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
        // If selectAll, pre-select all products
        if (selectAll) {
          setSelectedProductIds(
            new Set(data.products.map((p: Product) => p.id)),
          );
        }
      }
    } catch (err) {
      console.error("[EditCampaign] Products fetch error:", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductToggle = (productId: string, checked: boolean) => {
    setSelectedProductIds((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(productId);
      else newSet.delete(productId);
      return newSet;
    });
  };

  const handleSelectAllToggle = () => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedProductIds(new Set());
    } else {
      setSelectAll(true);
      setSelectedProductIds(new Set(products.map((p) => p.id)));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        name: formData.name,
        budget: {
          ...campaign.budget,
          daily_limit: formData.daily_budget,
          monthly_limit: formData.monthly_budget,
        },
        bidding: {
          ...campaign.bidding,
          bid_amount: formData.bid_amount,
        },
        targeting: {
          ...campaign.targeting,
          categories: formData.categories
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          keywords: formData.keywords
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          product_ids: selectAll ? [] : Array.from(selectedProductIds),
          all_products: selectAll,
        },
      });
      onClose();
    } catch (err) {
      console.error("[EditCampaign] Save error:", err);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedCount = selectAll ? products.length : selectedProductIds.size;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <div>
            <h2 className="text-lg font-semibold" style={{ color: "#171717" }}>
              Edit Campaign
            </h2>
            <p className="text-sm" style={{ color: "#A9A9A9" }}>
              Step {step} of 2:{" "}
              {step === 1 ? "Select Products" : "Campaign Details"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XIcon />
          </button>
        </div>

        {step === 1 && (
          <div className="flex-1 flex flex-col p-6 overflow-hidden">
            <div className="mb-4">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={selectAll}
                  onChange={handleSelectAllToggle}
                />
                <span
                  className="ml-2 text-sm font-medium"
                  style={{ color: "#171717" }}
                >
                  Promote All Products ({products.length})
                </span>
              </label>
            </div>

            {!selectAll && (
              <>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search products..."
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: "rgba(169,169,169,0.3)" }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {loadingProducts ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-gray-600" />
                  </div>
                ) : (
                  <div
                    className="flex-1 overflow-y-auto border"
                    style={{ borderColor: "rgba(169,169,169,0.2)" }}
                  >
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0"
                        style={{ borderColor: "rgba(169,169,169,0.1)" }}
                      >
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4"
                          checked={selectedProductIds.has(product.id)}
                          onChange={(e) =>
                            handleProductToggle(product.id, e.target.checked)
                          }
                        />
                        <img
                          src={product.image_url || "/images/placeholder.webp"}
                          alt={product.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p
                            className="text-sm font-medium"
                            style={{ color: "#171717" }}
                          >
                            {product.title}
                          </p>
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            {product.brand} - {product.category}
                          </p>
                        </div>
                        <p
                          className="text-sm font-semibold"
                          style={{ color: "#171717" }}
                        >
                          {formatCurrency(product.price)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div
              className="flex justify-end gap-3 pt-4 border-t mt-auto"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <button
                onClick={onClose}
                className="px-4 py-2 border text-sm font-medium hover:bg-gray-50"
                style={{
                  borderColor: "rgba(169,169,169,0.3)",
                  color: "#171717",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={selectedCount === 0}
                className="px-4 py-2 rounded-full text-sm font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: "#171717" }}
              >
                Next: Campaign Details ({selectedCount} products)
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex-1 flex flex-col p-6 overflow-y-auto">
            <div className="space-y-4 flex-1">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#171717" }}
                >
                  Campaign Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "#171717" }}
                  >
                    Daily Budget ($)
                  </label>
                  <input
                    type="number"
                    value={formData.daily_budget}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        daily_budget: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: "rgba(169,169,169,0.3)" }}
                    min="10"
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "#171717" }}
                  >
                    Monthly Budget ($)
                  </label>
                  <input
                    type="number"
                    value={formData.monthly_budget}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        monthly_budget: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: "rgba(169,169,169,0.3)" }}
                    min="100"
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#171717" }}
                >
                  Bid per Click ($)
                </label>
                <input
                  type="number"
                  value={formData.bid_amount}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      bid_amount: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                  min="0.01"
                  max="10.00"
                  step="0.01"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#171717" }}
                >
                  Target Categories
                </label>
                <input
                  type="text"
                  value={formData.categories}
                  onChange={(e) =>
                    setFormData({ ...formData, categories: e.target.value })
                  }
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                  placeholder="sneakers, jackets, outdoor gear"
                />
                <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
                  Comma-separated list
                </p>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#171717" }}
                >
                  Target Keywords
                </label>
                <input
                  type="text"
                  value={formData.keywords}
                  onChange={(e) =>
                    setFormData({ ...formData, keywords: e.target.value })
                  }
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                  placeholder="running, hiking, trail"
                />
                <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
                  Comma-separated list
                </p>
              </div>
            </div>

            <div
              className="flex gap-3 pt-4 border-t mt-auto"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <button
                onClick={() => setStep(1)}
                className="flex-1 px-4 py-2 border text-sm font-medium hover:bg-gray-50"
                style={{
                  borderColor: "rgba(169,169,169,0.3)",
                  color: "#171717",
                }}
              >
                Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 rounded-full text-sm font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: "#171717" }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SponsoredCampaignDetail() {
  const { id } = Route.useParams();
  const [campaign, setCampaign] = useState<SponsoredCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CAMPAIGNS_API}/campaigns/${id}`);
      if (response.ok) {
        const data = await response.json();
        setCampaign(data);
      } else {
        setCampaign(null);
      }
    } catch {
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: "active" | "paused") => {
    if (!campaign) return;
    try {
      const response = await fetch(`${CAMPAIGNS_API}/campaigns/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        setCampaign({ ...campaign, status: newStatus });
      } else {
        const error = await response.json();
        alert(`Failed to update status: ${error.detail || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Failed to update status");
    }
  };

  const handleSave = async (updates: Partial<SponsoredCampaign>) => {
    if (!campaign) return;

    // Transform the updates to match the API's expected format
    const apiPayload: Record<string, unknown> = {};

    if (updates.name) apiPayload.name = updates.name;
    if (updates.budget) {
      apiPayload.daily_budget = updates.budget.daily_limit;
      apiPayload.monthly_budget = updates.budget.monthly_limit;
    }
    if (updates.bidding) {
      apiPayload.bid_amount = updates.bidding.bid_amount;
    }
    if (updates.targeting) {
      apiPayload.categories = updates.targeting.categories;
      apiPayload.keywords = updates.targeting.keywords;
      apiPayload.product_ids = updates.targeting.product_ids;
      apiPayload.all_products = updates.targeting.all_products;
    }

    console.log("[EditCampaign] Saving to API:", apiPayload);

    const response = await fetch(`${CAMPAIGNS_API}/campaigns/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(apiPayload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to save");
    }

    const result = await response.json();
    console.log("[EditCampaign] API Response:", result);

    // Merge local updates
    setCampaign({ ...campaign, ...updates });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
          style={{ borderColor: "#171717" }}
        />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="text-center py-12">
          <p className="text-lg" style={{ color: "#A9A9A9" }}>
            Campaign not found
          </p>
          <Link
            to="/sponsored"
            className="text-sm underline mt-4 inline-block"
            style={{ color: "#171717" }}
          >
            Back to Sponsored Ads
          </Link>
        </div>
      </div>
    );
  }

  const ctr =
    campaign.performance.impressions > 0
      ? (campaign.performance.clicks / campaign.performance.impressions) * 100
      : 0;

  const costPerClick =
    campaign.performance.clicks > 0
      ? campaign.budget.total_spent / campaign.performance.clicks
      : 0;

  const dailyBudgetUsed =
    (campaign.budget.spent_today / campaign.budget.daily_limit) * 100;
  const monthlyBudgetUsed =
    (campaign.budget.spent_month / campaign.budget.monthly_limit) * 100;

  const productCount = campaign.targeting?.all_products
    ? "All Products"
    : `${campaign.targeting?.product_ids?.length || 0} Products`;

  return (
    <div className="max-w-5xl p-6">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/sponsored"
          className="inline-flex items-center gap-2 text-sm mb-4 hover:underline"
          style={{ color: "#A9A9A9" }}
        >
          <ArrowLeftIcon />
          Back to Sponsored Ads
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
              {campaign.name}
            </h1>
            <p className="text-sm mt-1" style={{ color: "#A9A9A9" }}>
              Brand: {campaign.brand_name} • Created:{" "}
              {new Date(campaign.created_at).toLocaleDateString()} •{" "}
              {productCount}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border hover:bg-gray-50"
              style={{ borderColor: "rgba(169,169,169,0.3)", color: "#171717" }}
            >
              <EditIcon />
              Edit
            </button>
            {campaign.status === "active" ? (
              <button
                onClick={() => handleStatusChange("paused")}
                className="px-4 py-2 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: "#E65100" }}
              >
                Pause Campaign
              </button>
            ) : (
              <button
                onClick={() => handleStatusChange("active")}
                className="px-4 py-2 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: "#2E7D32" }}
              >
                Resume Campaign
              </button>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-4">
          <span
            className="px-3 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor:
                campaign.status === "active" ? "#E8F5E9" : "#FFF3E0",
              color: campaign.status === "active" ? "#2E7D32" : "#E65100",
            }}
          >
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </span>
        </div>
      </div>

      {/* Performance Stats */}
      <div className="grid grid-cols-5 gap-4 mb-8">
        <StatCard
          label="Impressions"
          value={formatNumber(campaign.performance.impressions)}
        />
        <StatCard
          label="Clicks"
          value={formatNumber(campaign.performance.clicks)}
        />
        <StatCard label="CTR" value={`${ctr.toFixed(2)}%`} />
        <StatCard
          label="Total Spend"
          value={formatCurrency(campaign.budget.total_spent)}
        />
        <StatCard label="Avg CPC" value={formatCurrency(costPerClick)} />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Budget */}
        <div
          className="p-6"
          style={{
            backgroundColor: "#FAFAFA",
            border: "1px solid rgba(169,169,169,0.2)",
          }}
        >
          <h2 className="text-lg font-medium mb-4" style={{ color: "#171717" }}>
            Budget
          </h2>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: "#A9A9A9" }}>Today</span>
                <span style={{ color: "#171717" }}>
                  {formatCurrency(campaign.budget.spent_today)} /{" "}
                  {formatCurrency(campaign.budget.daily_limit)}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, dailyBudgetUsed)}%`,
                    backgroundColor:
                      dailyBudgetUsed > 90 ? "#E65100" : "#00A693",
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: "#A9A9A9" }}>This Month</span>
                <span style={{ color: "#171717" }}>
                  {formatCurrency(campaign.budget.spent_month)} /{" "}
                  {formatCurrency(campaign.budget.monthly_limit)}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, monthlyBudgetUsed)}%`,
                    backgroundColor:
                      monthlyBudgetUsed > 90 ? "#E65100" : "#00A693",
                  }}
                />
              </div>
            </div>

            <div
              className="pt-2 border-t"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div className="flex justify-between text-sm">
                <span style={{ color: "#A9A9A9" }}>All Time Spend</span>
                <span className="font-medium" style={{ color: "#171717" }}>
                  {formatCurrency(campaign.budget.total_spent)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bidding */}
        <div
          className="p-6"
          style={{
            backgroundColor: "#FAFAFA",
            border: "1px solid rgba(169,169,169,0.2)",
          }}
        >
          <h2 className="text-lg font-medium mb-4" style={{ color: "#171717" }}>
            Bidding
          </h2>

          <div className="space-y-3">
            <DetailRow
              label="Model"
              value={campaign.bidding.model.toUpperCase()}
            />
            <DetailRow
              label="Bid Amount"
              value={formatCurrency(campaign.bidding.bid_amount)}
            />
            <DetailRow
              label="Max Bid"
              value={formatCurrency(campaign.bidding.max_bid)}
            />
            <DetailRow
              label="Avg CPC Achieved"
              value={formatCurrency(costPerClick)}
            />
          </div>
        </div>

        {/* Targeting */}
        <div
          className="p-6"
          style={{
            backgroundColor: "#FAFAFA",
            border: "1px solid rgba(169,169,169,0.2)",
          }}
        >
          <h2 className="text-lg font-medium mb-4" style={{ color: "#171717" }}>
            Targeting
          </h2>

          <div className="space-y-4">
            <div>
              <p
                className="text-xs font-medium mb-2"
                style={{ color: "#A9A9A9" }}
              >
                Products
              </p>
              <span className="px-2 py-1 bg-blue-100 rounded text-xs text-blue-800">
                {productCount}
              </span>
            </div>

            <div>
              <p
                className="text-xs font-medium mb-2"
                style={{ color: "#A9A9A9" }}
              >
                Categories
              </p>
              <div className="flex flex-wrap gap-2">
                {campaign.targeting.categories?.length > 0 ? (
                  campaign.targeting.categories.map((cat, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-gray-100 rounded text-xs"
                      style={{ color: "#171717" }}
                    >
                      {cat}
                    </span>
                  ))
                ) : (
                  <span className="text-xs" style={{ color: "#A9A9A9" }}>
                    Any category
                  </span>
                )}
              </div>
            </div>

            <div>
              <p
                className="text-xs font-medium mb-2"
                style={{ color: "#A9A9A9" }}
              >
                Keywords
              </p>
              <div className="flex flex-wrap gap-2">
                {campaign.targeting.keywords?.length > 0 ? (
                  campaign.targeting.keywords.map((kw, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-gray-100 rounded text-xs"
                      style={{ color: "#171717" }}
                    >
                      {kw}
                    </span>
                  ))
                ) : (
                  <span className="text-xs" style={{ color: "#A9A9A9" }}>
                    Any keyword
                  </span>
                )}
              </div>
            </div>

            <div
              className="pt-2 border-t"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <DetailRow
                label="Min Semantic Score"
                value={`${((campaign.targeting.min_semantic_score || 0.7) * 100).toFixed(0)}%`}
              />
            </div>
          </div>
        </div>

        {/* Boost Settings */}
        <div
          className="p-6"
          style={{
            backgroundColor: "#FAFAFA",
            border: "1px solid rgba(169,169,169,0.2)",
          }}
        >
          <h2 className="text-lg font-medium mb-4" style={{ color: "#171717" }}>
            Boost Settings
          </h2>

          <div className="space-y-3">
            <DetailRow
              label="Max Position Boost"
              value={`${campaign.boost_config?.max_position_boost || 10} positions`}
            />
            <DetailRow
              label="Max Sponsored Slots"
              value={`${campaign.boost_config?.max_sponsored_slots || 3} per search`}
            />
          </div>

          <div className="mt-4 p-3 bg-blue-50">
            <p className="text-xs text-blue-700">
              <strong>Semantic Relevance Guarantee:</strong> Your products only
              appear when they genuinely match the user's search intent (≥
              {((campaign.targeting.min_semantic_score || 0.7) * 100).toFixed(
                0,
              )}
              % semantic similarity).
            </p>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditCampaignModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        campaign={campaign}
        onSave={handleSave}
      />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="p-4"
      style={{
        backgroundColor: "#FAFAFA",
        border: "1px solid rgba(169,169,169,0.2)",
      }}
    >
      <p className="text-xs font-medium mb-1" style={{ color: "#A9A9A9" }}>
        {label}
      </p>
      <p className="text-2xl font-light" style={{ color: "#171717" }}>
        {value}
      </p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm" style={{ color: "#A9A9A9" }}>
        {label}
      </span>
      <span className="text-sm font-medium" style={{ color: "#171717" }}>
        {value}
      </span>
    </div>
  );
}

export const Route = createFileRoute("/sponsored_/$id")({
  component: SponsoredCampaignDetail,
});
